import { useQuery } from 'react-query';

import { type QueryFunction, useRequest } from '@frontend/api';
import { queryClient } from '@frontend/api/src/common/client';

import { PartBotResults, PartBotResultJson, PartUuid } from './types';
import { formatPartBotResults } from './utils';

type UseGetPartBotResultsProps = {
  partUuid: PartUuid;
  partFileUuids?: string[];
};

export const PART_BOT_RESULTS_BASE_QUERY_KEY = ['partBotResults'];

export const getUsePartBotResultsKey = ({
  partUuid,
  partFileUuids,
}: UseGetPartBotResultsProps) =>
  partFileUuids
    ? [...PART_BOT_RESULTS_BASE_QUERY_KEY, partUuid, partFileUuids.join(',')]
    : [...PART_BOT_RESULTS_BASE_QUERY_KEY, partUuid];

export const invalidatePartBotResultsQuery = (props?: {
  partUuid?: string;
  partFileUuids?: string[];
}) => {
  const key = props?.partUuid
    ? getUsePartBotResultsKey({
        partUuid: props.partUuid,
        partFileUuids: props.partFileUuids,
      })
    : PART_BOT_RESULTS_BASE_QUERY_KEY;
  queryClient.invalidateQueries(key);
};

export const useGetPartBotResults: QueryFunction<
  PartBotResults,
  [UseGetPartBotResultsProps],
  ReturnType<typeof getUsePartBotResultsKey>
> = ({ partUuid, partFileUuids }, { queryOptions, requestOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    queryKey: getUsePartBotResultsKey({ partUuid, partFileUuids }),
    queryFn: ({ signal }) =>
      request<PartBotResultJson[]>(`/parts/${partUuid}/inferences`, {
        init: {
          signal,
        },
      }).then(formatPartBotResults),
  });
};
